import {
  averageRatingValue,
  fetchSupabaseRatings,
  fetchSupabaseWatchlist,
} from './supabaseData';
import { sampleCatalogForRecommendations } from './catalogQueries';

const THIS_YEAR = new Date().getFullYear();

function splitGenres(value) {
  return String(value || '')
    .split(',')
    .map((genre) => genre.trim())
    .filter(Boolean);
}

// Average only the type's actual rating criteria. Enriched rating rows also
// carry nullable metadata (director/creator/etc.), and Number(null) === 0, so
// averaging "every numeric-looking field" silently dragged scores down — a
// 5-star book came out at 3.75 and never counted as liked.
function getRatingStrength(rating) {
  return averageRatingValue(rating || {});
}

// Watchlist status has no 1-5 scale, so map it onto the same strength axis ratings use.
function watchlistStrength(status) {
  if (status === 'watched' || status === 'read') return 4.5;
  if (status === 'watching' || status === 'reading') return 4.0;
  if (status === 'plan_to_watch' || status === 'plan_to_read') return 3.3;
  return 3.0;
}

// Calendar-time backstop: a signal that's genuinely ancient counts for less,
// but this alone doesn't stop a large pile of old signals from outvoting a few
// new ones — that's what rankWeight below is for.
function decayWeight(dateStr, halfLifeDays = 200) {
  if (!dateStr) return 0.6;
  const days = (Date.now() - new Date(dateStr).getTime()) / 86400000;
  if (!Number.isFinite(days) || days < 0) return 1;
  return Math.max(0.25, Math.pow(0.5, days / halfLifeDays));
}

// Recency-by-order: ratings/watchlist arrives sorted newest-first, so index doubles
// as rank. Falls off fast so the last handful of things you did dominates taste
// signals no matter how large your older history is — a user with 200 old ratings
// and 3 new ones should get picks driven by the 3 new ones, not drowned out by volume.
function rankWeight(rank) {
  return Math.max(0.08, Math.pow(0.72, rank));
}

function buildSignals(ratings, watchlist) {
  const ratingSignals = ratings.map((rating, rank) => ({
    mediaType: rating.media_type,
    title: rating.title,
    genre: rating.genre,
    director: rating.director,
    creator: rating.creator,
    rawStrength: getRatingStrength(rating),
    weight: rankWeight(rank) * decayWeight(rating.created_at),
  }));

  const watchlistSignals = watchlist.map((item, rank) => ({
    mediaType: item.media_type,
    title: item.title,
    genre: item.genre,
    director: item.director,
    creator: item.creator,
    rawStrength: watchlistStrength(item.status),
    weight: rankWeight(rank) * decayWeight(item.updated_at || item.added_at, 260),
  }));

  return [...ratingSignals, ...watchlistSignals];
}

function weightedGenreCounts(signals, mediaType = null) {
  const counts = new Map();
  signals.forEach((signal) => {
    if (mediaType && signal.mediaType !== mediaType) return;
    if (signal.rawStrength < 3.2) return;
    splitGenres(signal.genre).forEach((genre) => {
      counts.set(genre, (counts.get(genre) || 0) + signal.weight);
    });
  });
  return [...counts.entries()].sort((a, b) => b[1] - a[1]).map(([genre]) => genre);
}

const TYPE_LABELS = { movie: 'movies', tv_show: 'TV shows', book: 'books' };

function buildTasteProfile(signals, mediaType = null) {
  const relevant = mediaType ? signals.filter((signal) => signal.mediaType === mediaType) : signals;
  const positive = relevant.filter((signal) => signal.rawStrength >= 4);

  if (!positive.length) {
    return mediaType
      ? `You haven't rated or tracked much in ${TYPE_LABELS[mediaType]} yet, so these lean on your activity elsewhere plus overall quality.`
      : 'You have started building your library, but there is not enough strong preference data yet. Rate a few titles or mark some as watched and your recommendations will sharpen up.';
  }

  const favoriteGenres = weightedGenreCounts(positive, mediaType).slice(0, 3);

  if (mediaType) {
    if (!favoriteGenres.length) {
      return `These lean into the ${TYPE_LABELS[mediaType]} you've rated and tracked most positively.`;
    }
    return `Lately you've been gravitating toward ${favoriteGenres.join(', ')} in ${TYPE_LABELS[mediaType]}. These picks are pulled from your most recent activity, not just your all-time history.`;
  }

  const typeWeights = new Map();
  positive.forEach((signal) => {
    typeWeights.set(signal.mediaType, (typeWeights.get(signal.mediaType) || 0) + signal.weight);
  });
  const favoriteTypes = [...typeWeights.entries()]
    .sort((left, right) => right[1] - left[1])
    .slice(0, 2)
    .map(([label]) => TYPE_LABELS[label]);

  if (!favoriteGenres.length) {
    return `You tend to enjoy ${favoriteTypes.join(' and ')} lately, so these picks lean into the parts of that library you seem to enjoy most.`;
  }

  return `Lately you've been gravitating toward ${favoriteGenres.join(', ')}, especially in ${favoriteTypes.join(' and ')}. These picks are pulled from your most recent ratings and watchlist activity, not just your all-time history.`;
}

// Movies expose 'director', TV shows expose 'creator' — different column,
// same role for matching purposes ("more from someone whose work you liked").
function getCreatorName(record) {
  return record.director || record.creator || '';
}

function scoreCandidate(candidate, signals, excludedKeys) {
  const candidateKey = `${candidate.media_type}:${candidate.id}`;
  if (excludedKeys.has(candidateKey)) {
    return -Infinity;
  }

  const candidateGenres = splitGenres(candidate.genre);
  const candidateCreator = getCreatorName(candidate).trim().toLowerCase();
  let score = 0;

  signals.forEach((signal) => {
    // Positive weight for liked/watched, negative for disliked/low-rated — recency-scaled either way.
    const polarity = signal.rawStrength >= 4 ? 1.5 : signal.rawStrength >= 3.2 ? 0.6 : -1.0;
    const weight = polarity * signal.weight;

    const sharedGenres = candidateGenres.filter((genre) => splitGenres(signal.genre).includes(genre));
    score += sharedGenres.length * 2.5 * weight;

    if (candidate.media_type === signal.mediaType && polarity > 0) {
      score += 0.6 * signal.weight;
    }

    // Same director/creator as something the user rated or saved — a much
    // stronger signal than genre overlap, since it's specific to that person's
    // body of work rather than a broad category.
    const signalCreator = getCreatorName(signal).trim().toLowerCase();
    if (candidateCreator && signalCreator && candidateCreator === signalCreator) {
      score += 4 * weight;
    }
  });

  // Quality nudge from catalog metadata (movies only expose these columns today).
  const quality = Number(candidate.vote_average) || (Number(candidate.popularity) ? Math.min(Number(candidate.popularity), 100) / 10 : 0);
  if (quality) {
    score += Math.min(quality, 10) * 0.15;
  }

  // Slight recency bonus — prefer titles from last 20 years over very old ones.
  if (candidate.year) {
    const age = THIS_YEAR - Number(candidate.year);
    score += Math.max(0, (20 - age)) / 20;
  }

  // Jitter breaks ties differently on every call so refreshing surfaces new-but-relevant picks
  // instead of freezing on the exact same top-N forever.
  score *= 0.9 + Math.random() * 0.2;

  return score;
}

function buildRecommendationReason(candidate, signals) {
  const candidateGenres = splitGenres(candidate.genre);

  const matches = signals
    .map((signal) => ({
      signal,
      overlap: candidateGenres.filter((genre) => splitGenres(signal.genre).includes(genre)).length,
    }))
    .filter((match) => match.overlap > 0 && match.signal.rawStrength >= 3.2)
    .sort((a, b) => (b.overlap * b.signal.weight * b.signal.rawStrength) - (a.overlap * a.signal.weight * a.signal.rawStrength));

  const best = matches[0]?.signal;

  if (best?.title) {
    const sharedGenre = candidateGenres.find((genre) => splitGenres(best.genre).includes(genre));
    const verb = best.rawStrength >= 4.3 ? 'loved' : best.rawStrength >= 4 ? 'liked' : 'added';
    if (sharedGenre) {
      return `If you ${verb} the ${sharedGenre.toLowerCase()} in ${best.title}, this has the same energy.`;
    }
    return `Fans of ${best.title} tend to love this one too.`;
  }

  if (candidateGenres.length) {
    const topLiked = signals
      .filter((signal) => signal.rawStrength >= 4)
      .sort((a, b) => b.weight - a.weight)[0];
    if (topLiked?.title) {
      return `Based on how much you enjoyed ${topLiked.title}, this ${candidateGenres[0].toLowerCase()} pick should be right up your alley.`;
    }
    return `A strong pick from the ${candidateGenres.slice(0, 2).join(' / ').toLowerCase()} catalog based on your recent activity.`;
  }

  return 'A strong match based on the patterns in your recent activity.';
}

function dedupeById(items) {
  const seen = new Set();
  const out = [];
  items.forEach((item) => {
    if (seen.has(item.id)) return;
    seen.add(item.id);
    out.push(item);
  });
  return out;
}

// Each pool is one random window over the whole catalog plus one per favorite
// genre, so every call can reach the full catalog rather than the same first page.
async function sampleTypePool(mediaType, genres, limit, kidsSafe = false) {
  const results = await Promise.all([
    sampleCatalogForRecommendations(mediaType, { limit, kidsSafe }),
    ...genres.map((genre) => sampleCatalogForRecommendations(mediaType, { genre, limit, kidsSafe })),
  ]);
  return dedupeById(results.flat());
}

const sampleMoviePool = (genres, kidsSafe = false) => sampleTypePool('movie', genres, 60, kidsSafe);
const sampleShowPool = (genres, kidsSafe = false) => sampleTypePool('tv_show', genres, 60, kidsSafe);
const sampleBookPool = (genres) => sampleTypePool('book', genres, 40);

function qualityScore(item) {
  return Number(item.vote_average) || (Number(item.popularity) ? Math.min(Number(item.popularity), 100) / 10 : 0);
}

// Cold-start fallback for users with no ratings/watchlist yet — instead of an
// empty "unlock recommendations" dead end, surface something reasonable:
// highest-rated for movies (the only type with a quality column), newest
// releases for TV/books.
async function fetchPopularForType(mediaType, limit, kidsSafe = false) {
  if (mediaType === 'movie') {
    const pool = await sampleCatalogForRecommendations('movie', { limit: 80, kidsSafe });
    return pool
      .map((item) => ({ ...item, media_type: 'movie' }))
      .sort((a, b) => qualityScore(b) - qualityScore(a))
      .slice(0, limit);
  }
  if (mediaType === 'tv_show') {
    const pool = await sampleCatalogForRecommendations('tv_show', { limit: 80, kidsSafe });
    return pool
      .map((item) => ({ ...item, media_type: 'tv_show' }))
      .sort((a, b) => (Number(b.year) || 0) - (Number(a.year) || 0))
      .slice(0, limit);
  }
  const pool = await sampleCatalogForRecommendations('book', { limit: 60 });
  return pool
    .map((item) => ({ ...item, media_type: 'book' }))
    .sort((a, b) => (Number(b.year) || 0) - (Number(a.year) || 0))
    .slice(0, limit);
}

function buildFallbackReason(candidate) {
  const quality = Number(candidate.vote_average);
  if (Number.isFinite(quality) && quality > 0) {
    return `Highly rated (${quality.toFixed(1)}/10) — a strong starting point.`;
  }
  if (candidate.year && Number(candidate.year) >= THIS_YEAR - 1) {
    return 'A recent release worth checking out.';
  }
  return 'Popular with other users right now.';
}

const COLD_START_MESSAGE = "You haven't rated or tracked anything yet, so these are popular picks to get you started — rate a few titles and your recommendations will personalize.";
const EMPTY_MESSAGE = 'Rate some movies, TV shows, or books — or add a few to your watchlist — to unlock personalized recommendations.';

async function prepareRecommendationContext() {
  const [ratings, watchlist] = await Promise.all([
    fetchSupabaseRatings(),
    fetchSupabaseWatchlist(),
  ]);

  if (!ratings.length && !watchlist.length) {
    return null;
  }

  const signals = buildSignals(ratings, watchlist);
  const excludedKeys = new Set([
    ...ratings.map((rating) => `${rating.media_type}:${rating.media_id}`),
    ...watchlist.map((item) => `${item.media_type}:${item.media_id}`),
  ]);

  return { signals, excludedKeys };
}

function topScored(items, mediaType, signals, excludedKeys, n) {
  return items
    .map((item) => ({ ...item, media_type: mediaType, _score: scoreCandidate({ ...item, media_type: mediaType }, signals, excludedKeys) }))
    // Only drop already-rated/tracked titles (-Infinity). A low score just means a
    // weaker match — still rank it rather than returning an empty list to someone
    // with a large history whose dislikes happen to outweigh the pool.
    .filter((candidate) => Number.isFinite(candidate._score))
    .sort((a, b) => b._score - a._score)
    .slice(0, n);
}

function toRecommendation(candidate, signals) {
  return {
    id: candidate.id,
    title: candidate.title,
    media_type: candidate.media_type,
    year: candidate.year,
    genre: candidate.genre,
    posterUrl: candidate.poster_url || candidate.cover_url || null,
    siteUrl:
      candidate.media_type === 'movie'
        ? `/movie/${candidate.id}`
        : candidate.media_type === 'tv_show'
          ? `/tv-show/${candidate.id}`
          : `/book/${candidate.id}`,
    reason: buildRecommendationReason(candidate, signals),
  };
}

export async function generateSupabaseRecommendations() {
  const context = await prepareRecommendationContext();
  if (!context) {
    const [movies, shows, books] = await Promise.all([
      fetchPopularForType('movie', 5),
      fetchPopularForType('tv_show', 5),
      fetchPopularForType('book', 2),
    ]);
    const recommendations = [...movies, ...shows, ...books].map((candidate) => ({
      id: candidate.id,
      title: candidate.title,
      media_type: candidate.media_type,
      year: candidate.year,
      genre: candidate.genre,
      posterUrl: candidate.poster_url || candidate.cover_url || null,
      siteUrl: candidate.media_type === 'movie' ? `/movie/${candidate.id}`
        : candidate.media_type === 'tv_show' ? `/tv-show/${candidate.id}`
        : `/book/${candidate.id}`,
      reason: buildFallbackReason(candidate),
    }));
    return {
      recommendations,
      tasteProfile: null,
      message: recommendations.length ? COLD_START_MESSAGE : EMPTY_MESSAGE,
    };
  }
  const { signals, excludedKeys } = context;

  const movieGenres = weightedGenreCounts(signals, 'movie').slice(0, 2);
  const showGenres = weightedGenreCounts(signals, 'tv_show').slice(0, 2);
  const bookGenres = weightedGenreCounts(signals, 'book').slice(0, 1);

  const [movies, shows, books] = await Promise.all([
    sampleMoviePool(movieGenres),
    sampleShowPool(showGenres),
    sampleBookPool(bookGenres),
  ]);

  // Enforce a mix: at least 4 movies, at least 4 TV shows, fill remainder with best overall
  const topMovies = topScored(movies, 'movie', signals, excludedKeys, 5);
  const topShows  = topScored(shows,  'tv_show', signals, excludedKeys, 5);
  const topBooks  = topScored(books,  'book', signals, excludedKeys, 2);
  const combined  = [...topMovies, ...topShows, ...topBooks]
    .sort((a, b) => b._score - a._score)
    .slice(0, 10);

  const recommendations = combined.map((candidate) => toRecommendation(candidate, signals));

  return {
    tasteProfile: buildTasteProfile(signals),
    recommendations,
    message: recommendations.length ? '' : 'No strong matches were found yet. Try rating a few more titles to sharpen your recommendations.',
  };
}

const TYPE_POOL_CONFIG = {
  movie: { sample: sampleMoviePool, genreSlice: 3 },
  tv_show: { sample: sampleShowPool, genreSlice: 3 },
  book: { sample: sampleBookPool, genreSlice: 2 },
};

// Deep, single-type recommendation list — used by the "Movies only" / "TV only" / "Books only"
// tabs so they surface a real top-12 for that type instead of duplicating the mixed Home list.
// kidsSafe restricts movie/tv_show pools to the same age_rating allowlist as
// the catalog browse pages — books have no rating data to filter on.
export async function generateSupabaseTypeRecommendations(mediaType, kidsSafe = false) {
  const config = TYPE_POOL_CONFIG[mediaType];
  if (!config) {
    throw new Error(`Unsupported media type: ${mediaType}`);
  }

  const context = await prepareRecommendationContext();
  if (!context) {
    const popular = await fetchPopularForType(mediaType, 12, kidsSafe);
    const recommendations = popular.map((candidate) => ({
      id: candidate.id,
      title: candidate.title,
      media_type: candidate.media_type,
      year: candidate.year,
      genre: candidate.genre,
      posterUrl: candidate.poster_url || candidate.cover_url || null,
      siteUrl: mediaType === 'movie' ? `/movie/${candidate.id}`
        : mediaType === 'tv_show' ? `/tv-show/${candidate.id}`
        : `/book/${candidate.id}`,
      reason: buildFallbackReason(candidate),
    }));
    return {
      recommendations,
      tasteProfile: null,
      message: recommendations.length ? COLD_START_MESSAGE : EMPTY_MESSAGE,
    };
  }
  const { signals, excludedKeys } = context;

  const genres = weightedGenreCounts(signals, mediaType).slice(0, config.genreSlice);
  const pool = await config.sample(genres, kidsSafe);
  const topItems = topScored(pool, mediaType, signals, excludedKeys, 12);
  const recommendations = topItems.map((candidate) => toRecommendation(candidate, signals));

  return {
    tasteProfile: buildTasteProfile(signals, mediaType),
    recommendations,
    message: recommendations.length ? '' : `No strong ${TYPE_LABELS[mediaType]} matches yet. Try rating a few more titles to sharpen your recommendations.`,
  };
}
